import { Injectable } from '@angular/core';
import { Observable, iif, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { marked } from 'marked';
import { EntityInfoService } from '../../../shared/services/metadata-api/entity-info.service';
import { Entity } from '../../../shared/models/entity.interface';
import {
  AIMessage,
  SourceDoc,
  Fact,
  ChatDetailsResponse,
  ChatMessage,
  ChatAIMessage,
} from '../interfaces/chart-details.interface';

@Injectable({
  providedIn: 'root',
})
export class ChatDataProcessorService {
  constructor(private entityInfoService: EntityInfoService) {}

  /**
   * Process LLM response and get entity information
   */
  processLLMResponse(response: AIMessage): Observable<{
    response: AIMessage;
    entityName: string[] | null;
  }> {
    const processedResponse = JSON.parse(JSON.stringify(response));

    // Update chat history with marked content
    if (processedResponse.history && processedResponse.history.length > 0) {
      const lastHistoryIndex = processedResponse.history.length - 1;
      if (processedResponse.history[lastHistoryIndex][1]) {
        processedResponse.history[lastHistoryIndex][1] = marked.parse(
          processedResponse.history[lastHistoryIndex][1],
        );
      }
    }

    return iif(
      () =>
        processedResponse.requested_entities &&
        processedResponse.requested_entities.length > 0,
      this.entityInfoService
        .getEntitiesInfo(processedResponse.requested_entities || [], 'array')
        .pipe(
          map((entitiesRes) => {
            const entities = entitiesRes as Entity[];
            const entityName = entities.map((entity) => entity.name);
            return { response: processedResponse, entityName };
          }),
        ),
      of({ response: processedResponse, entityName: null }),
    );
  }

  /**
   * Extract company information from requested entities
   */
  extractCompanyInfo(requestedEntities: string[]): {
    companyName: string;
    companyType: string;
  } {
    let companyName = '';
    let companyType = '';

    if (requestedEntities && requestedEntities.length > 0) {
      const firstEntity = requestedEntities[0];
      const [type, name] = firstEntity.split('.');
      companyName = name;
      companyType = type;
    }

    return { companyName, companyType };
  }

  /**
   * Process articles with events and citations
   */
  articlesWithEventsAndCitations(aiMessage: AIMessage): SourceDoc[] {
    const addedArticleIds = new Set<string>();
    const sourceDocumentsWithEvents = aiMessage.source_documents?.reduce(
      (acc, article) => {
        if (!addedArticleIds.has(article.id)) {
          let matchingEntityNames = [];
          const matchingEvents = aiMessage.events
            ?.filter((event) => event.document_ids.includes(article.id))
            .map((event) => event.name);

          if (aiMessage.requested_entities?.length > 1) {
            matchingEntityNames = aiMessage.events
              ?.filter((event) => event.document_ids.includes(article.id))
              .map((event) => event.entity_name);
          }

          acc.push({
            ...article,
            eventNames: matchingEvents || [],
            entityNames: matchingEntityNames,
          });

          addedArticleIds.add(article.id);
        }
        return acc;
      },
      [],
    );

    // Add citations
    let factCitationCounter = 1;
    aiMessage?.facts?.forEach((fact: Fact) => {
      if (fact.source_doc_ids.length > 0) {
        fact.source_doc_ids.map((source) => {
          const sourceDoc = sourceDocumentsWithEvents.find(
            (doc: SourceDoc) => doc.id === source,
          );
          if (sourceDoc) {
            if (!sourceDoc.citation) {
              sourceDoc.citation = factCitationCounter++;
            }
          }
        });
      }
    });

    // Sort documents
    if (sourceDocumentsWithEvents?.length > 0) {
      sourceDocumentsWithEvents.sort((a, b) => {
        if (a.citation !== undefined && b.citation !== undefined) {
          return a.citation - b.citation;
        }
        if (a.citation !== undefined) {
          return -1;
        }
        if (b.citation !== undefined) {
          return 1;
        }
        return (
          new Date(b.publish_time).getTime() -
          new Date(a.publish_time).getTime()
        );
      });
    }

    return sourceDocumentsWithEvents || [];
  }

  /**
   * Process chat message with additional data
   */
  processChatMessage(
    message: AIMessage,
    articlesToUse?: SourceDoc[],
    companyInfo?: { companyName: string; companyType: string },
  ): AIMessage {
    const processedMessage = { ...message };

    if (articlesToUse) {
      processedMessage.source_documents_events = articlesToUse;
    }

    if (companyInfo) {
      processedMessage.companyName = companyInfo.companyName;
      processedMessage.companyType = companyInfo.companyType;
    }

    return processedMessage;
  }

  /**
   * Parse JSON string safely
   */
  parseJsonString(jsonString: string): unknown {
    try {
      return JSON.parse(jsonString);
    } catch (e) {
      console.error('Error parsing JSON string:', e);
      return jsonString;
    }
  }

  /**
   * Check if message is AI message
   */
  isAIMessage(message: string | AIMessage): message is AIMessage {
    return (message as AIMessage).entityName !== undefined;
  }

  /**
   * Process chat details from API response
   */
  processChatDetails(
    chatDetails: ChatDetailsResponse,
    chatId: string,
    chatIdHistory: [string, string][],
    onChatTitleUpdate: (title: string, chatId: string) => void,
  ): {
    processedMessages: (ChatMessage | ChatAIMessage)[];
    updatedHistory: [string, string][];
  } {
    let chatIdQuestion = '';
    let chatIdAnswer: string | null = '';
    const updatedHistory = [...chatIdHistory];

    const processedMessages = chatDetails.messages.map((chat, index) => {
      if (chat.actor === 'human') {
        const currentMessage = this.isAIMessage(chat.message)
          ? ''
          : (chat.message as string);
        const nextMessage =
          index + 1 < chatDetails.messages.length
            ? chatDetails.messages[index + 1]
            : null;

        if (nextMessage && nextMessage.actor === 'ai') {
          chatIdQuestion = currentMessage;
        } else {
          if (updatedHistory.length >= 10) {
            updatedHistory.shift();
          }
          updatedHistory.push([currentMessage, '']);
        }
      } else if (chat.actor === 'ai') {
        let parsedMessage: AIMessage | string = chat.message;

        if (typeof chat.message === 'string') {
          try {
            parsedMessage = this.parseJsonString(chat.message) as AIMessage;
            (chat as ChatAIMessage).message = parsedMessage;
          } catch (e) {
            console.error('Error parsing AI message:', e);
          }
        }

        if (typeof parsedMessage === 'object') {
          const companyInfo = this.extractCompanyInfo(
            parsedMessage.requested_entities,
          );
          const articlesToUse =
            this.articlesWithEventsAndCitations(parsedMessage);
          (chat as ChatAIMessage).message = this.processChatMessage(
            parsedMessage,
            articlesToUse,
            companyInfo,
          );

          // Extract answer from AI message
          if (parsedMessage.summary?.text?.length > 0) {
            chatIdAnswer = parsedMessage.summary.text;
          } else if (
            !parsedMessage.summary &&
            parsedMessage.messages?.length > 0
          ) {
            if (typeof parsedMessage.messages[0] === 'string') {
              chatIdAnswer = parsedMessage.messages[0];
            }
          }

          // Update history if we have both question and answer
          if (chatIdQuestion && chatIdAnswer) {
            if (updatedHistory.length >= 10) {
              updatedHistory.shift();
            }
            updatedHistory.push([chatIdQuestion, chatIdAnswer]);

            chatIdQuestion = '';
            chatIdAnswer = '';
          }
        }
      }

      return chat;
    });

    // Set chat title if this is the first exchange
    if (updatedHistory.length === 1) {
      const chatTitle = updatedHistory[0][0];
      onChatTitleUpdate(chatTitle, chatId);
    }

    return {
      processedMessages,
      updatedHistory,
    };
  }
}
